import React from 'react'
import PropTypes from 'prop-types'

import { MuiThemeProvider, createMuiTheme } from '@material-ui/core/styles'
import {
  green,
  red,
  amber,
} from '@material-ui/core/colors'

const defaultTheme = createMuiTheme()
const theme = createMuiTheme({
  typography: {
    // Make sure to change app.css font as well
    fontFamily: 'GT Walsheim',
    h1: {
      color: '#222',
      fontSize: '25px',
      fontWeight: 700,
      lineHeight: '1.3em',
      [defaultTheme.breakpoints.up('md')]: {
        fontSize: '38px',
      },
    },
    h2: {
      color: '#666',
      fontSize: '18px',
      fontWeight: 500,
      lineHeight: '1.5em',
      [defaultTheme.breakpoints.up('md')]: {
        fontSize: '22px',
      },
    },
  },
  primary: {
    color: '#333',
  },
  secondary: {
    color: '#666',
  },
  a: {
    color: '#3f51b5',
    textDecoration: 'underline',
    '&:hover': {
      textDecoration: 'none',
    },
    cursor: 'pointer',
  },
  loginLink: {
    color: '#000',
    textDecoration: 'none',
    fontSize: '18px',
    fontWeight: 500,
    borderBottom: '3px solid #D4B33C',
    '&:hover': {
      textDecoration: 'none',
      borderBottom: '3px solid #000',
    },
  },
  introCardRoot: {
    maxWidth: '760px',
    minHeight: '560px',
    // Unfortunately, this is necessary because of :last-child styling in mui's CardContent root
    // This padding should match introCardContent padding, below
    '& .MuiCardContent-root:last-child': {
      [defaultTheme.breakpoints.down('sm')]: {
        padding: defaultTheme.spacing(3),
      },
      [defaultTheme.breakpoints.up('md')]: {
        padding: defaultTheme.spacing(5),
      },
    },
  },
  introCardMedia: {
    background: '#000',
    [defaultTheme.breakpoints.down('sm')]: {
      height: '120px',
      '& img': {
        height: '60px',
      },
    },
    [defaultTheme.breakpoints.down('xs')]: {
      '& img': {
        height: '40px',
      },
    },
    [defaultTheme.breakpoints.up('md')]: {
      height: '194px',
      '& img': {
        height: '90px',
      },
    },
  },
  introCardContent: {
    [defaultTheme.breakpoints.down('sm')]: {
      padding: defaultTheme.spacing(3),
    },
    [defaultTheme.breakpoints.up('md')]: {
      padding: defaultTheme.spacing(5),
    },
    '& ul': {
      margin: 0,
      paddingInlineStart: '20px',
      '& li': {
        margin: defaultTheme.spacing(1, 0),
      },
    },
  },
  introLink: {
    color: '#000',
    textDecoration: 'none',
    fontSize: '18px',
    fontWeight: 500,
    borderBottom: '3px solid #fff',
    '&:hover': {
      textDecoration: 'none',
      borderBottom: '3px solid #000',
    },
  },
  formControlLabelRoot: {
    margin: 0,
    alignItems: 'flex-start',
  },
  label: {
    fontSize: '12px',
    color: '#333',
    textTransform: 'uppercase',
    margin: defaultTheme.spacing(0, 0, 0.5, 0),
  },
  loginTextFieldRoot: {
    '& input:valid': {
      boxSizing: 'border-box',
      fontSize: '16px',
      fontWeight: 400,
      color: '#222',
      padding: defaultTheme.spacing(1, 2),
      height: '44px',
    },
    '& input + fieldset': {
      border: '1px solid #C4C4C4',
      borderRadius: 3,
    },
    '& input:valid:hover + fieldset': {
      border: '1px solid #000',
    },
    '& input:valid:focus + fieldset': {
      border: '1px solid #D5B33C',
      boxShadow: '0px 0px 9px rgba(255, 216, 80, 0.4)',
    },
    '& div.Mui-error fieldset': {
      border: '1px solid #E01E5A',
      boxShadow: '0px 0px 9px rgba(224, 30, 90, 0.4)',
    },
  },
  buttonContained: {
    height: '48px',
    overflow: 'hidden',
    padding: defaultTheme.spacing(1, 2),
    fontSize: '18px',
    fontWeight: 700,
    borderRadius: '2px',
    background: '#000',
    color: '#fff',
    boxShadow: 'none',
    textTransform: 'none',
    '&:disabled': {
      background: 'rgba(0, 0, 0, 0.3)',
      color: '#fff',
    },
    '&:hover': {
      background: '#444',
    },
    '& svg, & img': {
      marginLeft: defaultTheme.spacing(2),
    },
  },
  clickable: {
    cursor: 'pointer',
    '&:hover': {
      boxShadow: defaultTheme.shadows[2],
      '& td': {
        borderBottomColor: '#9d9d9d',
      },
    },
  },
  clickableGridItem: {
    boxShadow: defaultTheme.shadows[0],
    cursor: 'pointer',
    zIndex: 0,
    '&:hover': {
      boxShadow: defaultTheme.shadows[2],
      zIndex: 1,
    },
  },
  paper: {
    marginTop: defaultTheme.spacing(3),
    padding: defaultTheme.spacing(1, 3, 3),
  },
  status: {
    ok: { color: green[600] },
    warning: { color: amber[600] },
    error: { color: red[600] },
  },
  overrides: {
    MuiTooltip: {
      tooltip: {
        backgroundColor: 'black',
      },
    },
    MuiPaper: {
      root: {
        color: '#333',
      },
    },
    MuiTable: {
      root: {
        [defaultTheme.breakpoints.up('xs')]: {
          '& th, & td': {
            paddingLeft: 5,
            paddingRight: 5,
          },
        },
        [defaultTheme.breakpoints.up('sm')]: {
          '& th, & td': {
            paddingLeft: 10,
            paddingRight: 10,
          },
        },
        [defaultTheme.breakpoints.up('md')]: {
          '& th, & td': {
            paddingLeft: defaultTheme.spacing(2),
            paddingRight: defaultTheme.spacing(2),
          },
        },
        [defaultTheme.breakpoints.up('lg')]: {
          '& th, & td': {
            paddingLeft: defaultTheme.spacing(3),
            paddingRight: defaultTheme.spacing(3),
          },
        },
      },
    },
    MuiTableCell: {
      head: {
        fontSize: defaultTheme.typography.pxToRem(14),
      },
    },
  },
})

class Theme extends React.Component {
  static propTypes = {
    children: PropTypes.node.isRequired,
  }

  render() {
    const { children } = this.props
    return (
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    )
  }
}

export default Theme
